<script setup lang="ts">
/**
 * Marketing contact / demo-request page.
 *
 * Reuses the existing PublicContactController::store endpoint
 * (POST /api/public/contact) — it accepts a nullable tenant_id and is
 * already CORS- + throttle-friendly. We just compose the richer demo-request
 * fields (agency, team size, vertical, plan interest) into the subject +
 * message fields the backend expects, so no schema change is needed.
 */
definePageMeta({ layout: 'marketing' })

useSeoMeta({
  title: 'Talk to us — Caseflow',
  description: 'Book a demo, ask about Enterprise pricing, or just say hello. We reply within one business day.',
  ogTitle: 'Talk to us — Caseflow',
  ogDescription: 'Book a demo or ask about Enterprise pricing. We reply within one business day.',
  ogImage: '/og-image.png',
})

const form = reactive({
  name: '',
  email: '',
  phone: '',
  agency: '',
  vertical: '',
  team_size: '',
  plan_interest: '',
  message: '',
})

const submitting = ref(false)
const submitted = ref(false)
const error = ref<string | null>(null)

const verticals = [
  ['migration-agencies',   'Migration agency'],
  ['education-agencies',   'Education agency'],
  ['law-firms',            'Law firm'],
  ['real-estate-agencies', 'Real estate agency'],
  ['other',                'Something else'],
]
const teamSizes  = ['Just me', '2–5', '6–15', '16–50', '50+']
const planTiers  = ['Starter', 'Growth', 'Scale', 'Enterprise', 'Not sure yet']

const submit = async () => {
  if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
    error.value = 'Name, email, and a short message are required.'
    return
  }
  submitting.value = true
  error.value = null

  // Compose into the 5-field shape the existing backend already accepts.
  const subjectParts = [form.agency || null, form.vertical || null, form.plan_interest ? `Plan: ${form.plan_interest}` : null].filter(Boolean)
  const subject = subjectParts.length ? `Demo request — ${subjectParts.join(' · ')}` : 'Demo request'

  const messageBody = [
    `Vertical: ${form.vertical || '(unspecified)'}`,
    `Agency: ${form.agency || '(unspecified)'}`,
    `Team size: ${form.team_size || '(unspecified)'}`,
    `Plan interest: ${form.plan_interest || '(unspecified)'}`,
    '',
    form.message,
  ].join('\n')

  try {
    const config = useRuntimeConfig()
    const apiBase = (config.public as any).apiBaseUrl ?? ''
    await $fetch(`${apiBase}/api/public/contact`, {
      method: 'POST',
      body: {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        subject,
        message: messageBody,
      },
    } as any)
    submitted.value = true
    useAnalytics().track('Demo Request Submitted', {
      vertical: form.vertical || 'unspecified',
      team_size: form.team_size || 'unspecified',
      plan_interest: form.plan_interest || 'unspecified',
    })
  } catch (e: any) {
    const detail = e?.response?._data?.errors
    error.value = detail
      ? Object.values(detail).flat().join(' ')
      : (e?.response?._data?.message ?? 'Could not send. Please email us directly.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <section class="mx-auto max-w-3xl px-6 pt-20 pb-12 text-center">
    <p class="text-xs uppercase tracking-wider font-medium text-indigo-600">Talk to us</p>
    <h1 class="mt-3 text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
      Book a demo, ask about Enterprise, or just say hello.
    </h1>
    <p class="mt-4 text-gray-600 max-w-xl mx-auto">
      We reply within one business day. Real humans, no chatbots.
    </p>
  </section>

  <section class="mx-auto max-w-2xl px-6 pb-24">
    <!-- Success state -->
    <div
      v-if="submitted"
      class="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
    >
      <div class="w-12 h-12 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center text-2xl">✓</div>
      <h2 class="mt-4 text-xl font-semibold text-emerald-900">Got it — thanks!</h2>
      <p class="mt-2 text-sm text-emerald-800">
        We'll be in touch at <strong>{{ form.email }}</strong> within one business day.
        If it's urgent, reply to the confirmation email and it'll come straight to the team.
      </p>
      <div class="mt-6 flex gap-3 justify-center">
        <NuxtLink to="/" class="rounded-lg border border-emerald-300 px-4 py-2 text-sm text-emerald-700 hover:bg-white">Back to home</NuxtLink>
        <NuxtLink to="/blog" class="rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700">Read the blog</NuxtLink>
      </div>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="submit" class="space-y-5 rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
      <div v-if="error" class="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{{ error }}</div>

      <div class="grid md:grid-cols-2 gap-4">
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Your name *</span>
          <input v-model="form.name" type="text" required class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none" />
        </label>
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Email *</span>
          <input v-model="form.email" type="email" required class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none" />
        </label>
      </div>

      <div class="grid md:grid-cols-2 gap-4">
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Phone (optional)</span>
          <input v-model="form.phone" type="tel" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none" />
        </label>
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Agency name (optional)</span>
          <input v-model="form.agency" type="text" placeholder="Bloomfield Weddings" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none" />
        </label>
      </div>

      <div class="grid md:grid-cols-3 gap-4">
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Your vertical</span>
          <select v-model="form.vertical" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none">
            <option value="">— choose one —</option>
            <option v-for="[slug, label] in verticals" :key="slug" :value="label">{{ label }}</option>
          </select>
        </label>
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Team size</span>
          <select v-model="form.team_size" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none">
            <option value="">—</option>
            <option v-for="s in teamSizes" :key="s" :value="s">{{ s }}</option>
          </select>
        </label>
        <label class="block text-sm">
          <span class="text-gray-700 font-medium">Plan interest</span>
          <select v-model="form.plan_interest" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none">
            <option value="">—</option>
            <option v-for="p in planTiers" :key="p" :value="p">{{ p }}</option>
          </select>
        </label>
      </div>

      <label class="block text-sm">
        <span class="text-gray-700 font-medium">What do you want to talk about? *</span>
        <textarea v-model="form.message" rows="5" required placeholder="What you currently use, what's painful, what you'd want to see in a demo…" class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"></textarea>
      </label>

      <p class="text-xs text-gray-500">
        By submitting you agree to receive a reply from us. We never share your details, and we never add you to a marketing list without an explicit opt-in.
      </p>

      <button
        type="submit"
        :disabled="submitting"
        class="w-full rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 text-white px-5 py-3 text-sm font-medium hover:opacity-95 disabled:opacity-50"
      >
        {{ submitting ? 'Sending…' : 'Send message' }}
      </button>
    </form>

    <!-- Helpful alternates -->
    <div v-if="!submitted" class="mt-8 grid md:grid-cols-3 gap-4 text-sm">
      <div class="rounded-xl bg-gray-50 border border-gray-200 p-4">
        <p class="font-medium text-gray-900">Want a self-serve trial?</p>
        <p class="mt-1 text-gray-600">No demo call needed — just start the 14-day trial.</p>
        <NuxtLink to="/signup" class="mt-2 inline-block text-indigo-600 hover:underline">Start trial →</NuxtLink>
      </div>
      <div class="rounded-xl bg-gray-50 border border-gray-200 p-4">
        <p class="font-medium text-gray-900">Comparing plans?</p>
        <p class="mt-1 text-gray-600">All three tiers side-by-side with the full feature list.</p>
        <NuxtLink to="/pricing" class="mt-2 inline-block text-indigo-600 hover:underline">See pricing →</NuxtLink>
      </div>
      <div class="rounded-xl bg-gray-50 border border-gray-200 p-4">
        <p class="font-medium text-gray-900">Reading first?</p>
        <p class="mt-1 text-gray-600">Practical guides for each vertical, written by the team.</p>
        <NuxtLink to="/blog" class="mt-2 inline-block text-indigo-600 hover:underline">Read the blog →</NuxtLink>
      </div>
    </div>
  </section>
</template>
